import React, { useState, useRef, useEffect, useCallback } from 'react';
import styles from './styles.module.scss';
import { Chip } from '../Chip';

type EventData = {
  imageUrl: string;
  title: string;
  time: string;
  location: string;
  description: string;
  labels: string[];
};

type Props = {
  sectionTitle?: string;
  events: EventData[];
  onClose?: () => void;
  className?: string;
};

const EventCard: React.FC<Props> = ({ sectionTitle, events, onClose, className }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const descriptionWrapperRef = useRef<HTMLDivElement>(null);
  const isScrollingRef = useRef(false);
  const autoScrollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // 無限ループなしでイベントをそのまま使用
  const displayEvents = events;

  const handleScroll = useCallback(() => {
    if (!carouselRef.current || events.length <= 1 || isScrollingRef.current) return;

    const carousel = carouselRef.current;
    const scrollLeft = carousel.scrollLeft;
    const isDesktop = window.innerWidth >= 1024;
    const itemWidth = isDesktop ? 275 : 270;
    const gap = 20;

    const currentPosition = Math.round(scrollLeft / (itemWidth + gap));
    const validIndex = Math.max(0, Math.min(currentPosition, events.length - 1));
    setCurrentIndex(validIndex);
  }, [events.length]);

  // 次のスライドに移動する関数
  const moveToNextSlide = useCallback(() => {
    if (!carouselRef.current || events.length <= 1 || isScrollingRef.current) return;

    const nextIndex = (currentIndex + 1) % events.length;
    const carousel = carouselRef.current;
    const isDesktop = window.innerWidth >= 1024;
    const itemWidth = isDesktop ? 275 : 270;
    const gap = 20;
    const targetScrollLeft = nextIndex * (itemWidth + gap);

    isScrollingRef.current = true;
    carousel.scrollLeft = targetScrollLeft;
    setCurrentIndex(nextIndex);

    setTimeout(() => {
      isScrollingRef.current = false;
    }, 300);
  }, [currentIndex, events.length]);

  // 自動スクロールの設定
  useEffect(() => {
    if (events.length <= 1) return;

    // 自動スクロールを開始
    const startAutoScroll = () => {
      autoScrollIntervalRef.current = setInterval(() => {
        moveToNextSlide();
      }, 3000); // 3秒ごとにスライド
    };

    startAutoScroll();

    // カルーセルにマウスが乗ったら自動スクロールを停止
    const handleMouseEnter = () => {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
        autoScrollIntervalRef.current = null;
      }
    };

    // カルーセルからマウスが離れたら自動スクロールを再開
    const handleMouseLeave = () => {
      startAutoScroll();
    };

    const carousel = carouselRef.current;
    if (carousel) {
      carousel.addEventListener('mouseenter', handleMouseEnter);
      carousel.addEventListener('mouseleave', handleMouseLeave);
    }

    return () => {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
      }
      if (carousel) {
        carousel.removeEventListener('mouseenter', handleMouseEnter);
        carousel.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, [events.length, moveToNextSlide]);

  useEffect(() => {
    const carousel = carouselRef.current;
    if (carousel && events.length > 1) {
      carousel.style.scrollBehavior = 'auto';
      carousel.scrollLeft = 0;

      setTimeout(() => {
        carousel.style.scrollBehavior = 'smooth';
      }, 100);

      carousel.addEventListener('scroll', handleScroll);

      return () => {
        carousel.removeEventListener('scroll', handleScroll);
      };
    }
  }, [events.length, handleScroll]);

  // 説明部分のスクロール状態を監視
  const checkDescriptionScroll = useCallback(() => {
    if (descriptionRef.current) {
      const element = descriptionRef.current;
      const isScrollable = element.scrollHeight > element.clientHeight;
      const hasScrolled = element.scrollTop > 0; // スクロールされたかどうか

      // スクロール可能でない場合、またはスクロールされた場合はグラデーションを非表示
      setIsScrolledToBottom(!isScrollable || hasScrolled);
    }
  }, []);

  // 説明部分のスクロールイベントハンドラ
  const handleDescriptionScroll = useCallback(() => {
    checkDescriptionScroll();
  }, [checkDescriptionScroll]);

  // スクロールボタンのクリックハンドラ
  const handleScrollButtonClick = useCallback(() => {
    if (descriptionRef.current) {
      const element = descriptionRef.current;
      const scrollAmount = 100; // 100pxスクロール
      element.scrollTo({
        top: element.scrollTop + scrollAmount,
        behavior: 'smooth',
      });
    }
  }, []);

  // 現在のイベントが変わったときに説明部分のスクロール状態をリセット
  useEffect(() => {
    checkDescriptionScroll();
    if (descriptionRef.current) {
      descriptionRef.current.scrollTop = 0;
    }
  }, [currentIndex, checkDescriptionScroll]);

  // 説明部分のスクロールイベントリスナーを設定
  useEffect(() => {
    const descriptionElement = descriptionRef.current;
    if (descriptionElement) {
      descriptionElement.addEventListener('scroll', handleDescriptionScroll);
      // 初期状態をチェック
      checkDescriptionScroll();

      return () => {
        descriptionElement.removeEventListener('scroll', handleDescriptionScroll);
      };
    }
  }, [handleDescriptionScroll, checkDescriptionScroll]);

  // descriptionWrapperの高さに合わせてdescriptionのmax-heightを設定
  useEffect(() => {
    const updateDescriptionHeight = () => {
      if (descriptionWrapperRef.current && descriptionRef.current) {
        const wrapperHeight = descriptionWrapperRef.current.clientHeight;
        descriptionRef.current.style.maxHeight = `${wrapperHeight}px`;
        checkDescriptionScroll();
      }
    };

    updateDescriptionHeight();

    // リサイズ時にも更新
    window.addEventListener('resize', updateDescriptionHeight);

    return () => {
      window.removeEventListener('resize', updateDescriptionHeight);
    };
  }, [currentIndex, checkDescriptionScroll]);

  const currentEvent = events[currentIndex] || events[0];

  if (!currentEvent) {
    return null;
  }

  return (
    <div
      className={`${styles.card} ${onClose && !sectionTitle ? styles['card--modal'] : ''} ${className || ''}`}
    >
      {(sectionTitle || onClose) && (
        <div
          className={`${styles.header} ${onClose && !sectionTitle ? styles['header--modal'] : ''}`}
        >
          {sectionTitle && <h3 className={styles.sectionTitle}>{sectionTitle}</h3>}
          {onClose && (
            <button
              className={styles.closeButton}
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              aria-label="閉じる"
            >
              <svg
                width="28"
                height="28"
                viewBox="0 0 28 28"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="14" cy="14" r="13.5" fill="white" stroke="#0946F1" />
                <path
                  d="M18.644 20.082L14.018 15.456L9.392 20.082L8.096 18.786L12.722 14.16L8.096 9.534L9.392 8.238L14.018 12.864L18.644 8.238L19.94 9.534L15.314 14.16L19.94 18.786L18.644 20.082Z"
                  fill="#0946F1"
                />
              </svg>
            </button>
          )}
        </div>
      )}

      <div className={styles.content}>
        <div className={styles.carouselContainer}>
          <div ref={carouselRef} className={styles.imageCarousel}>
            {displayEvents.map((event, index) => (
              <div key={`${event.title}-${index}`} className={styles.carouselImageWrapper}>
                <div
                  className={styles.carouselImageBg}
                  style={{ backgroundImage: `url(${event.imageUrl})` }}
                />
                <img src={event.imageUrl} alt={event.title} className={styles.carouselImage} />
              </div>
            ))}
          </div>

          {events.length > 1 && (
            <div className={styles.indicators}>
              {events.map((_, index) => (
                <button
                  key={index}
                  className={`${styles.indicator} ${index === currentIndex ? styles.indicatorActive : ''}`}
                  onClick={() => {
                    if (carouselRef.current && !isScrollingRef.current) {
                      isScrollingRef.current = true;
                      const carousel = carouselRef.current;
                      const isDesktop = window.innerWidth >= 1024;
                      const itemWidth = isDesktop ? 275 : 270;
                      const gap = 20;
                      const targetScrollLeft = index * (itemWidth + gap);
                      carousel.scrollLeft = targetScrollLeft;
                      setCurrentIndex(index);
                      setTimeout(() => {
                        isScrollingRef.current = false;
                      }, 300);
                    }
                  }}
                  aria-label={`イベント ${index + 1} を表示`}
                />
              ))}
            </div>
          )}
        </div>

        <h4 className={styles.eventTitle}>
          {currentEvent.title.split('\n').map((line, index) => (
            <React.Fragment key={index}>
              {index > 0 && <br />}
              {line}
            </React.Fragment>
          ))}
        </h4>

        <div className={styles.separator} />

        {/* モバイル用：時間・場所情報を上に表示 */}
        <div className={styles.eventDetailsMobile}>
          <div className={styles.timeInfo}>
            <span className={styles.timeInfo__label}>時間：</span>
            <span className={styles.timeInfo__content}>
              {currentEvent.time.split('\n').map((line, index) => (
                <React.Fragment key={index}>
                  {index > 0 && <br />}
                  {line}
                </React.Fragment>
              ))}
            </span>
          </div>
          <div className={styles.locationInfo}>
            <span className={styles.locationInfo__label}>場所：</span>
            <span className={styles.locationInfo__content}>{currentEvent.location}</span>
          </div>
        </div>

        <div className={styles.contentLayout}>
          <div className={styles.scrollableContent}>
            <div ref={descriptionWrapperRef} className={styles.descriptionWrapper}>
              <div
                ref={descriptionRef}
                className={styles.description}
                data-scrolled-to-bottom={isScrolledToBottom}
                dangerouslySetInnerHTML={{ __html: currentEvent.description }}
              />

              {/* スクロールインジケーター */}
              <button
                className={`${styles.scrollIndicator} ${!isScrolledToBottom ? styles['scrollIndicator--visible'] : ''}`}
                onClick={handleScrollButtonClick}
                aria-label="下にスクロール"
              >
                <svg
                  width="30"
                  height="30"
                  viewBox="0 0 30 30"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="15" cy="15" r="15" transform="rotate(-180 15 15)" fill="#063986" />
                  <path
                    d="M14.8003 8.7998L14.6362 20.2291"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <path
                    d="M19.2002 15.1064L14.6454 20.7999"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <path
                    d="M10.0815 15.1064L14.6362 20.7999"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
          </div>

          <div className={styles.sidebarInfo}>
            {/* デスクトップ用：時間・場所情報 */}
            <div className={styles.eventDetailsDesktop}>
              <div className={styles.timeInfo}>
                <span className={styles.timeInfo__label}>時間：</span>
                <span className={styles.timeInfo__content}>
                  {currentEvent.time.split('\n').map((line, index) => (
                    <React.Fragment key={index}>
                      {index > 0 && <br />}
                      {line}
                    </React.Fragment>
                  ))}
                </span>
              </div>
              <div className={styles.locationInfo}>
                <span className={styles.locationInfo__label}>場所：</span>
                <span className={styles.locationInfo__content}>{currentEvent.location}</span>
              </div>
            </div>

            <div className={styles.categorySection}>
              <div className={styles.categoryTitle}>カテゴリー</div>

              <div className={styles.chips}>
                {currentEvent.labels.map((label) => (
                  <Chip key={label} text={label} variant="primary" isActive={true} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventCard;
